<script setup lang="ts">
import { CheckCircle2, Clock, Hourglass } from 'lucide-vue-next'
import { computed } from 'vue'

// Same KID for every club (22 digits, MOD10). Any OCR payment to the account verifies it.
const VERIFICATION_KID = '9999999999999999999991'

const props = defineProps<{
  businessName: string
  bban: string
}>()

const formattedBban = computed(() => {
  const digits = props.bban?.replace(/\D/g, '') ?? ''
  if (digits.length !== 11) return props.bban
  return `${digits.slice(0, 4)}.${digits.slice(4, 6)}.${digits.slice(6)}`
})
</script>
<template>
  <div class="flex flex-col gap-6">
    <div class="flex flex-col gap-1">
      <span class="text-slate-500 text-sm">Steg 2 av 2</span>
      <h3 class="text-lg font-semibold">Bekreft direktekontoen med en betaling på 1 kr</h3>
      <p class="text-sm text-slate-600">
        Avtalen er sendt til banken. Det som gjenstår er å bekrefte at vi kan lese innbetalinger til
        {{ businessName }} sin konto. Det gjør dere ved å betale 1 kr med KID-nummeret under.
      </p>
    </div>

    <div
      class="rounded-md border bg-slate-50 p-4 grid grid-cols-[auto_1fr] gap-x-6 gap-y-3 text-sm"
    >
      <span class="text-slate-500">Beløp</span>
      <span class="font-semibold">1,00 kr</span>
      <span class="text-slate-500">Til konto</span>
      <span class="font-semibold font-mono select-all">{{ formattedBban }}</span>
      <span class="text-slate-500">KID</span>
      <span class="font-semibold font-mono select-all break-all">{{ VERIFICATION_KID }}</span>
    </div>

    <ul class="flex flex-col gap-3 text-sm">
      <li class="flex gap-2">
        <Hourglass :size="18" class="shrink-0 text-slate-500 mt-0.5" />
        <span>
          Banken sender avtalen videre til Mastercard, så det tar ofte noen dager før den er aktiv.
          Har kontoen ikke blitt klar et par virkedager etter betalingen, kan avtalen fortsatt være
          under behandling. Da kan dere betale 1 kr på nytt senere.
        </span>
      </li>
      <li class="flex gap-2">
        <Clock :size="18" class="shrink-0 text-slate-500 mt-0.5" />
        <span>Innbetalinger registreres hos oss kl. 17 hver virkedag.</span>
      </li>
      <li class="flex gap-2">
        <CheckCircle2 :size="18" class="shrink-0 text-green-600 mt-0.5" />
        <span>
          Når vi mottar betalingen er oppsettet fullført. Kontoen settes automatisk til klar, og
          dere trenger ikke gjøre noe mer.
        </span>
      </li>
    </ul>

    <p class="text-xs text-slate-500">
      Betalingen kan gjøres fra hvilken som helst konto, av dere eller av oss. Bruk KID-feltet, ikke
      meldingsfeltet.
    </p>
  </div>
</template>
